import React, { useEffect, useMemo, useState } from 'react';
import { StepContainer, NavigationButtons, InfoBox, FormField } from '../index';
import { useWizard } from '../../wizard';
import { contentApi } from '../../services/contentApi';
import { contentKeys } from '../../constants/contentKeys';

const MIN_HEIGHT_CM = 30;
const MAX_HEIGHT_CM = 300;

const PassportHeightStep: React.FC = () => {
  const { selfServiceData, updateSelfServiceData, goNext, goBack } = useWizard();
  const [title, setTitle] = useState('Hvor høj er ansøgeren i centimeter?');
  const [body, setBody] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [localValue, setLocalValue] = useState<string>(() =>
    typeof selfServiceData.passportHeight === 'number' ? String(selfServiceData.passportHeight) : ''
  );

  const applicantLabel = useMemo(() => {
    if (selfServiceData.passportFor === 'child') {
      return 'Barnets højde i centimeter';
    }
    if (selfServiceData.passportFor === 'self' && selfServiceData.user?.name) {
      return `Din højde i centimeter (${selfServiceData.user.name})`;
    }
    return 'Højde i centimeter';
  }, [selfServiceData.passportFor, selfServiceData.user?.name]);

  useEffect(() => {
    let isMounted = true;
    contentApi
      .getByKey(contentKeys.pas.ask_height)
      .then((content) => {
        if (!isMounted || !content) {
          return;
        }
        if (content.title) {
          setTitle(content.title);
        }
        if (Array.isArray(content.body)) {
          setBody(content.body);
        }
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (typeof selfServiceData.passportHeight === 'number') {
      setLocalValue(String(selfServiceData.passportHeight));
    } else {
      setLocalValue('');
    }
  }, [selfServiceData.passportHeight]);

  const parseHeight = (raw: string): number | null => {
    const normalized = raw.replace(',', '.').trim();
    if (!normalized) {
      return null;
    }
    const numeric = Number(normalized);
    if (!Number.isFinite(numeric)) {
      return Number.NaN;
    }
    return Math.round(numeric);
  };

  const validateHeight = (raw: string): { value: number | null; error: string | null } => {
    const parsed = parseHeight(raw);
    if (parsed === null) {
      return { value: null, error: 'Angiv højden i centimeter.' };
    }
    if (Number.isNaN(parsed)) {
      return { value: null, error: 'Indtast venligst et gyldigt tal.' };
    }
    if (parsed < MIN_HEIGHT_CM || parsed > MAX_HEIGHT_CM) {
      return {
        value: parsed,
        error: `Højden skal være mellem ${MIN_HEIGHT_CM} og ${MAX_HEIGHT_CM} cm.`
      };
    }
    return { value: parsed, error: null };
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    setLocalValue(rawValue);

    if (!rawValue.trim()) {
      setError(null);
      updateSelfServiceData('passportHeight', undefined);
      return;
    }

    const { value, error: validationError } = validateHeight(rawValue);
    if (value !== null && !Number.isNaN(value)) {
      updateSelfServiceData('passportHeight', value);
    } else {
      updateSelfServiceData('passportHeight', undefined);
    }
    setError(validationError);
  };

  const handleNext = () => {
    const { value, error: validationError } = validateHeight(localValue);
    if (validationError || value === null || Number.isNaN(value)) {
      setError(validationError ?? 'Angiv højden i centimeter.');
      return;
    }

    updateSelfServiceData('passportHeight', value);
    setError(null);
    goNext();
  };

  const hasValidHeight =
    typeof selfServiceData.passportHeight === 'number' &&
    selfServiceData.passportHeight >= MIN_HEIGHT_CM &&
    selfServiceData.passportHeight <= MAX_HEIGHT_CM;

  return (
    <StepContainer title={title}>
      {body.length > 0 && (
        <InfoBox type="info">
          {body.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </InfoBox>
      )}
      <div className="form-field-wrapper">
        <label className="field-label" htmlFor="passportHeight">
          {applicantLabel}
        </label>
        <FormField
          type="number"
          id="passportHeight"
          name="passportHeight"
          value={localValue}
          placeholder="fx 172"
          error={error ?? undefined}
          onChange={handleChange}
          onBlur={handleChange}
          required
          min={MIN_HEIGHT_CM}
          max={MAX_HEIGHT_CM}
          step={1}
          inputMode="numeric"
        />
      </div>
      <NavigationButtons
        onBack={goBack}
        onNext={handleNext}
        nextDisabled={!hasValidHeight}
      />
    </StepContainer>
  );
};

export default PassportHeightStep;
